import React, { useState, useEffect, useRef } from 'react';
import { X, Save, AlertCircle, CheckCircle, EyeOff, Layout, Power } from 'lucide-react';
import { pagesApi, layoutsApi } from '../api/client';
import type { Page, PageLayout } from '../schemas/validation';
import {
  LAYOUT_NONE,
  LAYOUT_SITE_DEFAULT,
  getLayoutDropdownValue,
  getLayoutSelectionFromPage,
  hasPageLayoutSelectionChanged,
} from '../utils/pageLayoutSelection';

export type PageUpdatedMeta = {
  layoutSettingsChanged?: boolean;
};

interface EditPageModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: Page;
  onPageUpdated: (updatedPage: Page, meta?: PageUpdatedMeta) => void;
  onUnpublish?: (page: Page) => void;
}

export const EditPageModal: React.FC<EditPageModalProps> = ({
  isOpen,
  onClose,
  page,
  onPageUpdated,
  onUnpublish,
}) => {
  const showLayoutValue = page.showLayout !== false;
  const [formData, setFormData] = useState({
    title: page.title,
    slug: page.slug,
    noindex: page.noindex || false,
    layoutId: page.layoutId || (null as string | null),
    showLayout: showLayoutValue,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [layouts, setLayouts] = useState<PageLayout[]>([]);
  const [isLoadingLayouts, setIsLoadingLayouts] = useState(false);
  const [selectedLayoutValue, setSelectedLayoutValue] = useState(
    getLayoutDropdownValue(page.layoutId, showLayoutValue)
  );
  const initialLayoutSelectionRef = useRef(getLayoutSelectionFromPage(page));

  // Reset form when page changes and load layouts
  useEffect(() => {
    if (!isOpen) return;

    initialLayoutSelectionRef.current = getLayoutSelectionFromPage(page);

    const sl = page.showLayout !== false;
    setFormData({
      title: page.title,
      slug: page.slug,
      noindex: page.noindex || false,
      layoutId: page.layoutId || null,
      showLayout: sl,
    });
    setSelectedLayoutValue(getLayoutDropdownValue(page.layoutId, sl));
    setErrors({});
    loadLayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, page]);

  const loadLayouts = async () => {
    try {
      setIsLoadingLayouts(true);
      const response = await layoutsApi.list(page.siteId);
      setLayouts(response.layouts);
    } catch (error) {
      console.error('Failed to load layouts:', error);
      setLayouts([]);
    } finally {
      setIsLoadingLayouts(false);
    }
  };

  // Handle layout dropdown change - map sentinel values to layoutId / showLayout
  const handleLayoutChange = (value: string) => {
    setSelectedLayoutValue(value);
    if (value === LAYOUT_NONE) {
      setFormData(prev => ({ ...prev, layoutId: null, showLayout: false }));
    } else if (value === LAYOUT_SITE_DEFAULT) {
      setFormData(prev => ({ ...prev, layoutId: null, showLayout: true }));
    } else {
      // Specific layout UUID
      setFormData(prev => ({ ...prev, layoutId: value, showLayout: true }));
    }
  };

  const defaultLayout = layouts.find(l => l.isDefault);

  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      // Auto-generate slug if it matches the current generated slug or is empty
      slug: prev.slug === generateSlug(prev.title) || !prev.slug ? generateSlug(title) : prev.slug,
    }));

    // Clear title error when user starts typing
    if (errors.title) {
      setErrors(prev => ({ ...prev, title: '' }));
    }
  };

  const handleSlugChange = (slug: string) => {
    // Clean the slug as user types
    const cleanSlug = slug
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '') // Only allow letters, numbers, and hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

    setFormData(prev => ({ ...prev, slug: cleanSlug }));

    // Clear slug error when user starts typing
    if (errors.slug) {
      setErrors(prev => ({ ...prev, slug: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Page title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Page title must be less than 200 characters';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Page slug is required';
    } else if (formData.slug.length > 100) {
      newErrors.slug = 'Page slug must be less than 100 characters';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Page slug can only contain lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Update page basic info
      const updatedPage = await pagesApi.update(page.id, {
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        noindex: formData.noindex,
      });

      const layoutSettingsChanged = hasPageLayoutSelectionChanged(
        initialLayoutSelectionRef.current,
        selectedLayoutValue
      );

      let pageToReturn = updatedPage;

      // Update layout if changed
      if (layoutSettingsChanged) {
        await layoutsApi.setPageLayout(page.id, {
          layoutId: formData.layoutId,
          showLayout: formData.showLayout,
        });
        pageToReturn = await pagesApi.get(page.id);
      }

      // Show success toast
      setToastMessage('Page updated successfully!');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      onPageUpdated(pageToReturn, { layoutSettingsChanged });
      onClose();
    } catch (error: any) {
      console.error('Error updating page:', error);

      // Handle specific API errors
      if (error.status === 409) {
        setErrors({ slug: 'A page with this slug already exists' });
      } else {
        // Show error toast
        setToastMessage(
          error.status === 400
            ? error.message || 'Invalid page data'
            : 'Failed to update page. Please try again.'
        );
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Edit Page</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Page Title */}
          <div>
            <label htmlFor="page-title" className="block text-sm font-medium text-gray-700 mb-2">
              Page Title *
            </label>
            <input
              id="page-title"
              type="text"
              value={formData.title}
              onChange={e => handleTitleChange(e.target.value)}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.title ? 'border-red-300' : 'border-gray-300'
              } disabled:bg-gray-50 disabled:opacity-50`}
              placeholder="Enter page title"
            />
            {errors.title && (
              <div className="mt-1 flex items-center space-x-1 text-red-600 text-sm">
                <AlertCircle size={16} />
                <span>{errors.title}</span>
              </div>
            )}
          </div>

          {/* Page Slug */}
          <div>
            <label htmlFor="page-slug" className="block text-sm font-medium text-gray-700 mb-2">
              Page Slug *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                /
              </span>
              <input
                id="page-slug"
                type="text"
                value={formData.slug}
                onChange={e => handleSlugChange(e.target.value)}
                disabled={isLoading}
                className={`w-full pl-8 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.slug ? 'border-red-300' : 'border-gray-300'
                } disabled:bg-gray-50 disabled:opacity-50`}
                placeholder="page-slug"
              />
            </div>
            {errors.slug && (
              <div className="mt-1 flex items-center space-x-1 text-red-600 text-sm">
                <AlertCircle size={16} />
                <span>{errors.slug}</span>
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              This will be the URL path for your page. Only lowercase letters, numbers, and hyphens
              allowed.
            </p>
          </div>

          {/* Page Layout Selection */}
          <div className="pt-2 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center space-x-2">
                <Layout size={14} />
                <span>Page Layout</span>
              </span>
            </label>
            {isLoadingLayouts ? (
              <div className="flex items-center space-x-2 text-gray-500 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                <span>Loading layouts...</span>
              </div>
            ) : layouts.length === 0 ? (
              <p className="text-sm text-gray-500">
                No layouts available. Create a layout in Site Manager to use here.
              </p>
            ) : (
              <>
                <select
                  value={selectedLayoutValue}
                  onChange={e => handleLayoutChange(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:opacity-50"
                >
                  <option value={LAYOUT_SITE_DEFAULT}>
                    Site Default{defaultLayout ? ` (${defaultLayout.name})` : ' (none set)'}
                  </option>
                  <option value={LAYOUT_NONE}>No Layout (Standalone / Landing Page)</option>
                  {layouts.map(layout => (
                    <option key={layout.id} value={layout.id}>
                      {layout.name}
                    </option>
                  ))}
                </select>

                <p className="text-xs text-gray-500 mt-2">
                  {selectedLayoutValue === LAYOUT_SITE_DEFAULT
                    ? defaultLayout
                      ? `Uses your site's default layout: "${defaultLayout.name}" — includes header & footer.`
                      : 'Uses the site default layout. No default layout is currently set — page will have no header or footer until one is created.'
                    : selectedLayoutValue === LAYOUT_NONE
                      ? 'No header or footer. Ideal for landing pages.'
                      : 'Uses a specific layout for this page.'}
                </p>
              </>
            )}
          </div>

          {/* Search Engine Indexing (noindex) */}
          <div className="flex items-start space-x-3 pt-2">
            <div className="flex items-center h-5">
              <input
                id="noindex"
                type="checkbox"
                checked={formData.noindex}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    noindex: e.target.checked,
                  }))
                }
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="noindex" className="block text-sm font-medium text-gray-700">
                <span className="flex items-center space-x-2">
                  <EyeOff size={14} />
                  <span>Hide from Search Engines</span>
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, this page will be excluded from the sitemap and search engines will be
                asked not to index it.
              </p>
            </div>
          </div>

          {/* Remove from live site — only shown when page is published */}
          {page.publishedAt && onUnpublish && (
            <div className="pt-3 border-t">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Power size={18} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-amber-800">Remove from live site</h4>
                    <p className="text-xs text-amber-700 mt-1">
                      Unpublishes this page so it's no longer visible to visitors. You can republish
                      it later.
                    </p>
                    <button
                      type="button"
                      onClick={() => onUnpublish(page)}
                      disabled={isLoading}
                      className="mt-2 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-md transition-colors disabled:opacity-50"
                    >
                      Unpublish Page
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading || !formData.title.trim() || !formData.slug.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-4 right-4 z-60 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <CheckCircle size={20} />
          <span>{toastMessage}</span>
          <button
            onClick={() => setShowSuccessToast(false)}
            className="ml-2 text-green-700 hover:text-green-900"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Error Toast */}
      {showErrorToast && (
        <div className="fixed top-4 right-4 z-60 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <AlertCircle size={20} />
          <span>{toastMessage}</span>
          <button
            onClick={() => setShowErrorToast(false)}
            className="ml-2 text-red-700 hover:text-red-900"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};
